/**
 * DeepSeek price table + cost model (Blueprint v2 §3.4 / P10). Prices are a
 * snapshot (USD per 1M tokens) — re-verify V4-Pro on the live dashboard before
 * billing (the disputed promo rate was 0.435 / 0.003625 / 0.87). Cost is
 * cache-aware: the cached portion of input bills at a fraction of cache-miss.
 */
import type { UsageReport } from '@fim/futurex-protocol';

export interface ModelPrice {
  readonly inputPerM: number;
  readonly cachedInputPerM: number;
  readonly outputPerM: number;
}

const FLASH: ModelPrice = { inputPerM: 0.14, cachedInputPerM: 0.014, outputPerM: 0.28 };
const PRO: ModelPrice = { inputPerM: 1.74, cachedInputPerM: 0.0145, outputPerM: 3.48 };

export const PRICES: Readonly<Record<string, ModelPrice>> = {
  'deepseek-v4-flash': FLASH,
  'deepseek-v4-pro': PRO,
};

export function priceFor(model: string): ModelPrice {
  return PRICES[model] ?? FLASH;
}

/** USD cost of one call, given its usage. */
export function computeCost(model: string, usage: UsageReport): number {
  const p = priceFor(model);
  const cachedIn = usage.cacheHitTokens ?? 0;
  const uncachedIn = Math.max(0, usage.inputTokens - cachedIn);
  return (
    (uncachedIn * p.inputPerM + cachedIn * p.cachedInputPerM + usage.outputTokens * p.outputPerM) /
    1_000_000
  );
}

/** Running per-session usage totals. */
export interface UsageTotals {
  calls: number;
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  costUsd: number;
}

export function emptyTotals(): UsageTotals {
  return { calls: 0, inputTokens: 0, cachedInputTokens: 0, outputTokens: 0, reasoningTokens: 0, costUsd: 0 };
}

/**
 * Sum two usage reports field-by-field. An agentic turn is MANY model calls
 * (one per tool round); budget/billing must see the whole turn, not the last call.
 */
export function accumulateUsage(a: UsageReport, b: UsageReport): UsageReport {
  const opt = (x?: number, y?: number): number | undefined =>
    x === undefined && y === undefined ? undefined : (x ?? 0) + (y ?? 0);
  const cacheHitTokens = opt(a.cacheHitTokens, b.cacheHitTokens);
  const cacheMissTokens = opt(a.cacheMissTokens, b.cacheMissTokens);
  const reasoningTokens = opt(a.reasoningTokens, b.reasoningTokens);
  return {
    inputTokens: a.inputTokens + b.inputTokens,
    outputTokens: a.outputTokens + b.outputTokens,
    totalTokens: a.totalTokens + b.totalTokens,
    ...(cacheHitTokens !== undefined ? { cacheHitTokens } : {}),
    ...(cacheMissTokens !== undefined ? { cacheMissTokens } : {}),
    ...(reasoningTokens !== undefined ? { reasoningTokens } : {}),
  };
}

/** Fold one call's usage into the running totals. */
export function addUsage(totals: UsageTotals, model: string, usage: UsageReport): void {
  totals.calls += 1;
  totals.inputTokens += usage.inputTokens;
  totals.cachedInputTokens += usage.cacheHitTokens ?? 0;
  totals.outputTokens += usage.outputTokens;
  totals.reasoningTokens += usage.reasoningTokens ?? 0;
  totals.costUsd += computeCost(model, usage);
}

/** A compact, multi-field rendering of the totals (cache-hit ratio + cost). */
export function formatUsage(totals: UsageTotals): string[] {
  const ratio = totals.inputTokens > 0 ? Math.round((totals.cachedInputTokens / totals.inputTokens) * 100) : 0;
  return [
    `calls ${totals.calls}`,
    `in ${totals.inputTokens} (${ratio}% cached)`,
    `out ${totals.outputTokens}${totals.reasoningTokens ? ` (+${totals.reasoningTokens} reasoning)` : ''}`,
    `cost $${totals.costUsd.toFixed(4)}`,
  ];
}
